import { useMemo, useState, useEffect } from 'react'
import { useNavigate } from 'react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Box, Chip, CircularProgress, Divider, MenuItem, Stack, Tooltip, Typography, Avatar,
  IconButton,
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as CityIcon,
  People as PeopleIcon,
  OpenInNew as ViewIcon,
} from '@mui/icons-material'
import { toast } from 'react-toastify'
import { observer } from 'mobx-react-lite'
import { ConfirmDialog } from '../components/shared/ConfirmDialog'
import { EntityModal } from '../components/shared/EntityModal'
import { PageHeader } from '../components/shared/PageHeader'
import { DataTable, type Column } from '../components/ui/DataTable'
import {
  customersApi, Customer, CreateCustomerPayload, UpdateCustomerPayload,
  CUSTOMER_SOURCES, CustomerSource,
} from '../lib/api/customersApi'
import { themeColors } from '../app/layout/theme'
import { authStore } from '../lib/stores/authStore'
import { orgsApi, lobsApi } from '../lib/api/tenantsApi'
import { useOrgFilter, useOrgSelector } from '../context/OrgSelectorContext'
import { DeleteActionIcon, EditActionIcon } from '../components/ui/ActionIcons'
import { DeleteDialog } from '../components/shared/DeleteDialog'
import { PageActionButtons } from '../components/shared/PageActionButtons'
import { CompanyDrawer } from '../components/drawers/CompanyDrawer'
import { ListToolbar, type BulkAction } from '../components/shared/ListToolbar'
import { UnifiedFilters, EMPTY_UNIFIED_FILTERS, FilterDef, countActiveFilters } from '../components/shared/UnifiedFilterDialog'
import { FormInput, FormSelect } from '../components/ui'
import { usePermission } from '../lib/hooks/usePermission'
import { getCustomerTypes, type CustomerType } from '../lib/api/customerTypesApi'
import { maskPhoneNumber } from '../utils/phoneUtils'

const SOURCE_COLORS: Record<string, string> = {
  FACEBOOK: '#1877f2',
  WEBSITE: '#0070d2',
  REFERRAL: '#2e7d32',
  WALK_IN: '#f57c00',
  PHONE_CALL: '#7b1fa2',
  OTHER: '#757575',
}

const SOURCE_LABELS: Record<string, string> = {
  FACEBOOK: 'Facebook', WEBSITE: 'Website', REFERRAL: 'Referral',
  WALK_IN: 'Walk-in', PHONE_CALL: 'Phone Call', OTHER: 'Other',
}

function getInitials(name: string) {
  return name.split(' ').slice(0, 2).map(n => n[0]?.toUpperCase() ?? '').join('')
}

// ── Customer Form Dialog ──────────────────────────────────────────────────────
interface FormDialogProps {
  open: boolean
  customer?: Customer | null
  onClose: () => void
  onSave: (p: CreateCustomerPayload | UpdateCustomerPayload) => void
  isSaving: boolean
  isSuperAdmin?: boolean
}

const EMPTY_FORM = { name: '', phone: '', email: '', companyName: '', city: '', source: 'OTHER' as CustomerSource }

function CustomerFormDialog({ open, customer, onClose, onSave, isSaving, isSuperAdmin }: FormDialogProps) {
  const isEdit = !!customer
  const [form, setForm] = useState({ ...EMPTY_FORM })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [orgId, setOrgId] = useState('')
  const [isSaveConfirmOpen, setIsSaveConfirmOpen] = useState(false)

  const { data: orgs = [] } = useQuery({
    queryKey: ['admin-orgs'],
    queryFn: orgsApi.getAll,
    enabled: open && !!isSuperAdmin && !isEdit,
    staleTime: 60_000,
  })

  useMemo(() => {
    if (open) {
      setForm(customer
        ? {
          name: customer.name,
          phone: customer.phone,
          email: customer.email ?? '',
          companyName: customer.companyName ?? '',
          city: customer.city ?? '',
          source: (customer.source ?? 'OTHER') as CustomerSource,
        }
        : { ...EMPTY_FORM }
      )
      setErrors({})
      setOrgId('')
      setIsSaveConfirmOpen(false)
    }
  }, [open, customer])

  const set = (k: string, v: string) => {
    setForm(f => ({ ...f, [k]: v }))
    setErrors(e => ({ ...e, [k]: '' }))
  }

  const validate = () => {
    const e: Record<string, string> = {}
    if (!form.name.trim()) e.name = 'Name is required'
    if (!form.phone.trim()) e.phone = 'Phone is required'
    if (isSuperAdmin && !isEdit && !orgId) e.orgId = 'Organisation is required'
    return e
  }

  const handleSave = () => {
    const e = validate()
    if (Object.keys(e).length) { setErrors(e); return }
    if (isEdit) {
      setIsSaveConfirmOpen(true)
    } else {
      onSave({
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || undefined,
        companyName: form.companyName.trim() || undefined,
        city: form.city.trim() || undefined,
        source: form.source,
        ...(isSuperAdmin && !isEdit && orgId ? { orgId } : {}),
      })
    }
  }

  const handleConfirmSave = () => {
    setIsSaveConfirmOpen(false)
    onSave({
      name: form.name.trim(),
      phone: form.phone.trim(),
      email: form.email.trim() || undefined,
      companyName: form.companyName.trim() || undefined,
      city: form.city.trim() || undefined,
      source: form.source,
      ...(isSuperAdmin && !isEdit && orgId ? { orgId } : {}),
    })
  }

  return (
    <>
      <EntityModal
        open={open}
        onClose={onClose}
        onSubmit={handleSave}
        title={isEdit ? 'Edit Customer' : 'New Customer'}
        submitLabel={isEdit ? 'Save Changes' : 'Create Customer'}
        isSubmitting={isSaving}
        maxWidth="sm"
        icon={isEdit ? <EditIcon sx={{ fontSize: 18 }} /> : <AddIcon sx={{ fontSize: 18 }} />}
      >
        <Stack spacing={2}>
          <FormInput label="Full Name *" value={form.name} onChange={e => set('name', e.target.value)}
            error={!!errors.name} helperText={errors.name} fullWidth />
          <FormInput label="Phone *" value={form.phone} onChange={e => set('phone', e.target.value)}
            fieldType="phone" error={!!errors.phone} helperText={errors.phone || 'e.g. +919876543210'} fullWidth />
          <FormInput label="Email" value={form.email} onChange={e => set('email', e.target.value)} type="email" fullWidth />
          <FormInput label="Company Name" value={form.companyName} onChange={e => set('companyName', e.target.value)} fullWidth />
          <FormInput label="City" value={form.city} onChange={e => set('city', e.target.value)} fullWidth />
          
          <FormSelect
            label="Source"
            value={form.source}
            onChange={v => set('source', v)}
            options={CUSTOMER_SOURCES.map(s => ({ value: s, label: SOURCE_LABELS[s] }))}
            fullWidth
          />

          {isSuperAdmin && !isEdit && (
            <>
              <Divider sx={{ my: 0.5 }}>
                <Typography sx={{ fontSize: '11px', color: themeColors.text.muted, px: 1 }}>
                  Tenant Assignment
                </Typography>
              </Divider>
              <FormSelect
                label="Organisation *"
                value={orgId}
                onChange={v => {
                  setOrgId(v)
                  setErrors(er => ({ ...er, orgId: '' }))
                }}
                options={orgs.map(o => ({ value: o.id, label: o.name }))}
                error={!!errors.orgId}
                helperText={errors.orgId}
                placeholder="Select an organisation"
                fullWidth
              />
            </>
          )}
        </Stack>
      </EntityModal>

      <ConfirmDialog
        open={isSaveConfirmOpen}
        title="Save Changes"
        description="Are you sure you want to save the changes to this company?"
        confirmLabel="Save"
        confirmColor="primary"
        onClose={() => setIsSaveConfirmOpen(false)}
        onConfirm={handleConfirmSave}
      />
    </>
  )
}

// ── Customer List (DataTable view) ────────────────────────────────────────────
interface CustomerListViewProps {
  customers: Customer[]
  canEdit: boolean
  canDelete: boolean
  selected: Set<string>
  onSelectionChange: (selected: Set<string>) => void
  onView: (c: Customer) => void
  onEdit: (c: Customer) => void
  onDelete: (c: Customer) => void
  page: number
  pageSize: number
  total: number
  totalPages: number
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
}

function CustomerListView({
  customers, canEdit, canDelete, selected, onSelectionChange, onView, onEdit, onDelete,
  page, pageSize, total, totalPages, onPageChange, onPageSizeChange,
}: CustomerListViewProps) {
  const customerColumns: Column[] = [
    {
      id: 'companyName',
      label: 'Company Name',
      render: (_, customer: Customer) => customer.companyName ?? customer.name,
    },
    { id: 'phone', label: 'Phone Number', render: (_, customer: Customer) => maskPhoneNumber(customer.phone) },
    { id: 'email', label: 'Email', render: (_, customer: Customer) => customer.email ?? '-' },
    { id: 'city', label: 'City', render: (_, customer: Customer) => customer.city ?? '-' },
    { id: 'source', label: 'Source', render: (_, customer: Customer) => SOURCE_LABELS[customer.source] ?? customer.source },
    { id: 'owner', label: 'Created By', render: (_, customer: Customer) => customer.createdBy?.name ?? '-' },
    {
      id: 'actions',
      label: 'Actions',
      align: 'right',
      width: 112,
      sortable: false,
      render: (_, customer: Customer) => (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.25 }} onClick={e => e.stopPropagation()}>
          <Tooltip title="View details">
            <IconButton size="small" onClick={() => onView(customer)}
              sx={{ color: '#7A7580', '&:hover': { color: '#2F1E57', bgcolor: '#F2EEF9' } }}>
              <ViewIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
          {canEdit && (
            <Tooltip title="Edit">
              <IconButton size="small" onClick={() => onEdit(customer)}
                sx={{ color: '#7A7580', '&:hover': { color: '#2F1E57', bgcolor: '#F2EEF9' } }}>
                <EditActionIcon />
              </IconButton>
            </Tooltip>
          )}
          {canDelete && (
            <Tooltip title="Delete">
              <IconButton size="small" onClick={() => onDelete(customer)}
                sx={{ color: '#D92D20', '&:hover': { bgcolor: '#FFE5E5' } }}>
                <DeleteActionIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ]

  return (
    <DataTable
      columns={customerColumns}
      rows={customers}
      selectable
      selectedIds={selected}
      onSelectionChange={onSelectionChange}
      showRowNumbers={false}
      onRowClick={(customer: Customer) => onView(customer)}
      pagination={{
        page,
        pageSize,
        total,
        totalPages,
        itemLabel: 'results',
        onPageChange,
        onPageSizeChange,
        pageSizeOptions: [10, 20, 50, 100],
      }}
    />
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
const CustomersPage = observer(function CustomersPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()

  const { hasAction } = usePermission()
  // SuperAdmin detection: OrgId/LobId-based (matches the navbar org selector), not
  // usePermission's roleName-string match — that doesn't reliably match every
  // SuperAdmin's role label, which previously hid the Organisation/LOB filters here.
  const { isSuperAdmin } = useOrgSelector()
  const canCreate = hasAction('/customers', 'create')
  const canEdit   = hasAction('/customers', 'update')
  const canDelete = hasAction('/customers', 'delete')
  const canExport = hasAction('/customers', 'export')

  // Org Admin tier (mirrors backend ITenantContext convention): has an orgId but no
  // specific lobId — sees/filters across every LOB in their own org, unlike a Normal User.
  const userOrgId  = authStore.user?.orgId ?? ''
  const userLobId  = authStore.user?.lobId ?? ''
  const isOrgAdmin = !isSuperAdmin && !!userOrgId && !userLobId

  const navOrgFilter = useOrgFilter()

  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState<UnifiedFilters>(() => {
    const saved = sessionStorage.getItem('crm-customers-filters')
    if (saved) { try { return JSON.parse(saved) } catch {} }
    return EMPTY_UNIFIED_FILTERS
  })
  const [page, setPage] = useState(1)
  const [pageSizeTable, setPageSizeTable] = useState(() => {
    const saved = localStorage.getItem('crm_customers_page_size')
    return saved ? Number(saved) : 10
  })

  useEffect(() => {
    sessionStorage.setItem('crm-customers-filters', JSON.stringify(filters))
  }, [filters])
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [formOpen, setFormOpen] = useState(false)
  const [companyDrawerOpen, setCompanyDrawerOpen] = useState(false)
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null)
  const [deletingCustomer, setDeletingCustomer] = useState<Customer | null>(null)
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false)

  const { data: orgsData = [] } = useQuery({
    queryKey: ['admin-orgs'],
    queryFn: orgsApi.getAll,
    enabled: isSuperAdmin,
    staleTime: 60_000,
  })

  // Prefer the org picked in this page's own filter dialog; fall back to whatever
  // org is selected in the navbar so the LOB list works right after picking an org
  // up top, without needing to also set the local Organisation filter.
  const orgIdForFetching = filters.checkboxes.orgId?.[0] || navOrgFilter.orgId
  // SuperAdmin: LOBs depend on whichever org is in scope (filter or navbar selection).
  // Org Admin: LOBs are always scoped to their own (fixed) org — no org selector needed.
  const lobOrgIdForFetching = isSuperAdmin ? orgIdForFetching : userOrgId
  const { data: lobsForFilter = [] } = useQuery({
    queryKey: ['admin-lobs', lobOrgIdForFetching],
    queryFn: () => lobsApi.getAll(lobOrgIdForFetching!),
    enabled: (isSuperAdmin || isOrgAdmin) && !!lobOrgIdForFetching,
    staleTime: 30_000,
  })

  const effectiveOrgIds = isSuperAdmin && filters.checkboxes.orgId?.length
    ? filters.checkboxes.orgId
    : navOrgFilter.orgId ? [navOrgFilter.orgId] : undefined

  const effectiveLobIds = (isSuperAdmin || isOrgAdmin) && filters.checkboxes.lobId?.length
    ? filters.checkboxes.lobId
    : undefined

  const pageSize = pageSizeTable

  const { data: customerTypes = [] } = useQuery<CustomerType[]>({
    queryKey: ['customer-types'],
    queryFn: getCustomerTypes,
    staleTime: 5 * 60 * 1000,
  })

  const { data, isLoading } = useQuery({
    queryKey: ['customers', { search, filters, orgIds: effectiveOrgIds, lobIds: effectiveLobIds, page, pageSize }],
    queryFn: () => customersApi.getAll({
      search: search || undefined,
      sources: filters.checkboxes.source?.length ? filters.checkboxes.source : undefined,
      orgIds: effectiveOrgIds,
      lobIds: effectiveLobIds,
      ownerId: filters.ownerIds[0] || undefined,
      customerTypeIds: filters.checkboxes.customerTypeId?.length ? filters.checkboxes.customerTypeId.map(Number) : undefined,
      fromDate: filters.dateFrom ? `${filters.dateFrom}T00:00:00Z` : undefined,
      toDate: filters.dateTo ? `${filters.dateTo}T23:59:59Z` : undefined,
      page,
      pageSize,
    }),
    placeholderData: (prev) => prev,
  })

  const createMutation = useMutation({
    mutationFn: (p: CreateCustomerPayload) => customersApi.create(p),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customers'] })
      setFormOpen(false)
      setEditingCustomer(null)
      toast.success('Customer created')
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to create customer'),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, p }: { id: string; p: UpdateCustomerPayload }) => customersApi.update(id, p),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customers'] })
      setFormOpen(false)
      setEditingCustomer(null)
      toast.success('Customer updated')
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to update customer'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => customersApi.delete(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['customers'] }); setDeletingCustomer(null); toast.success('Customer deleted') },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to delete customer'),
  })

  const bulkDeleteMutation = useMutation({
    mutationFn: async () => {
      const ids = Array.from(selected)
      await Promise.all(ids.map(id => customersApi.delete(id)))
      return ids.length
    },
    onSuccess: (count) => {
      qc.invalidateQueries({ queryKey: ['customers'] })
      setSelected(new Set())
      setBulkDeleteOpen(false)
      toast.success(`${count} company/companies deleted`)
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to delete companies'),
  })

  const bulkActions: BulkAction[] = useMemo(() => {
    const actions: BulkAction[] = []
    if (canDelete) {
      actions.push({
        label: 'Delete',
        color: 'error',
        onClick: () => setBulkDeleteOpen(true),
      })
    }
    return actions
  }, [canDelete])

  const handleSave = (payload: CreateCustomerPayload | UpdateCustomerPayload) => {
    if (editingCustomer) {
      updateMutation.mutate({ id: editingCustomer.id, p: payload as UpdateCustomerPayload })
    } else {
      createMutation.mutate(payload as CreateCustomerPayload)
    }
  }

  const openCreate = () => { setEditingCustomer(null); setCompanyDrawerOpen(true) }
  const openEdit = (c: Customer) => { setEditingCustomer(c); setFormOpen(true) }

  const customers = data?.items ?? []
  const total = data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / pageSizeTable))

  const filterDefs: FilterDef[] = useMemo(() => {
    const defs: FilterDef[] = [
      {
        type: 'checkbox',
        key: 'source',
        label: 'Source',
        options: Array.from(new Set([...CUSTOMER_SOURCES, ...customers.map(c => c.source)]))
                 .map(s => ({ label: SOURCE_LABELS[s] || s, value: s })),
      },
      {
        type: 'checkbox',
        key: 'customerTypeId',
        label: 'Customer Type',
        options: customerTypes.map(ct => ({ label: ct.name, value: String(ct.id) })),
      },
    ]
    if (isSuperAdmin) {
      defs.push(
        {
          type: 'checkbox',
          key: 'orgId',
          label: 'Organisation',
          options: orgsData.map(o => ({ label: o.name, value: o.id })),
        },
        {
          type: 'checkbox',
          key: 'lobId',
          label: 'LOB',
          options: lobsForFilter.map(l => ({ label: l.name, value: l.id })),
        },
      )
    } else if (isOrgAdmin) {
      // Org Admin's org is fixed (no Organisation picker needed) — just let them
      // narrow the list down to one or more LOBs within their own org.
      defs.push({
        type: 'checkbox',
        key: 'lobId',
        label: 'LOB',
        options: lobsForFilter.map(l => ({ label: l.name, value: l.id })),
      })
    }
    if (isSuperAdmin || isOrgAdmin) defs.push({ type: 'owner', label: 'Owner' })
    defs.push({ type: 'date', label: 'Date Created' })
    return defs
  }, [isSuperAdmin, isOrgAdmin, orgsData, lobsForFilter, customers, customerTypes])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PageHeader
        title="Companies"
        breadcrumb="CRM"
        actions={
          (canCreate || canExport) ? (
            <PageActionButtons
              onNew={canCreate ? () => openCreate() : undefined}
              onExport={canExport ? async () => {
                const apiFilters = {
                  orgIds: effectiveOrgIds,
                  lobIds: effectiveLobIds,
                  sources: filters.checkboxes.source?.length ? filters.checkboxes.source : undefined,
                  fromDate: filters.dateFrom ? `${filters.dateFrom}T00:00:00Z` : undefined,
                  toDate: filters.dateTo ? `${filters.dateTo}T23:59:59Z` : undefined,
                }
                await customersApi.export({ ...apiFilters, search })
              } : undefined}
              newLabel="New Company"
            />
          ) : undefined
        }
      />


      {/* ── Pinned toolbar (search + filters) ── */}
      <Box sx={{ px: 3, pt: 3, pb: 0, flexShrink: 0 }}>
        <ListToolbar
          search={search}
          onSearch={(v) => { setSearch(v); setPage(1) }}
          searchPlaceholder="Search name, phone, email, company..."
          filterDefs={filterDefs}
          filters={filters}
          onFilterChange={(f) => { setFilters(f); setPage(1) }}
          selectedCount={selected.size}
          bulkActions={bulkActions}
          onClearSelection={() => setSelected(new Set())}
        />
      </Box>

      {/* ── Scrollable content ── */}
      <Box sx={{ flex: 1, px: 3, pb: 3, pt: 2, display: 'flex', flexDirection: 'column', minHeight: 0 }}>

        {/* Content */}
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>
        ) : customers.length === 0 ? (
          <Box sx={{ py: 10, textAlign: 'center', color: themeColors.text.secondary }}>
            <BusinessIcon sx={{ fontSize: 56, opacity: 0.15, mb: 1 }} />
            <Typography sx={{ fontWeight: 600 }}>No customers found</Typography>
            <Typography sx={{ fontSize: '13px', mt: 0.5 }}>
              {search || countActiveFilters(filters) > 0 ? 'Try adjusting your filters' : 'Create your first customer to get started'}
            </Typography>
          </Box>
        ) : (
          <CustomerListView
            customers={customers}
            canEdit={canEdit}
            canDelete={canDelete}
            selected={selected}
            onSelectionChange={setSelected}
            onView={(c) => navigate(`/customers/${c.id}`)}
            onEdit={openEdit}
            onDelete={(c) => setDeletingCustomer(c)}
            page={page}
            pageSize={pageSize}
            total={total}
            totalPages={totalPages}
            onPageChange={setPage}
            onPageSizeChange={s => { setPageSizeTable(s); localStorage.setItem('crm_customers_page_size', String(s)); setPage(1) }}
          />
        )}
      </Box>

      {/* New Company — drawer */}
      <CompanyDrawer
        open={companyDrawerOpen}
        onClose={() => setCompanyDrawerOpen(false)}
        isSuperAdmin={isSuperAdmin}
        onCreated={() => {
          qc.invalidateQueries({ queryKey: ['customers'] })
        }}
      />

      {/* Edit Company — legacy dialog (kept for edit flow) */}
      {editingCustomer && (
        <CustomerFormDialog
          open={formOpen}
          customer={editingCustomer}
          onClose={() => { setFormOpen(false); setEditingCustomer(null) }}
          onSave={handleSave}
          isSaving={updateMutation.isPending}
          isSuperAdmin={isSuperAdmin}
        />
      )}

      <DeleteDialog
        open={!!deletingCustomer}
        title="Delete Company"
        name={deletingCustomer?.companyName ?? deletingCustomer?.name}
        description={
          deletingCustomer
            ? `Delete ${deletingCustomer.companyName ?? deletingCustomer.name}? This will also remove all their contacts. This action cannot be undone.`
            : undefined
        }
        onClose={() => setDeletingCustomer(null)}
        onConfirm={() => deletingCustomer && deleteMutation.mutate(deletingCustomer.id)}
        isDeleting={deleteMutation.isPending}
      />

      <DeleteDialog
        open={bulkDeleteOpen}
        title={`Delete ${selected.size} Company/Companies`}
        description={`Are you sure you want to delete ${selected.size} selected company/companies? This will also remove all their contacts. This action cannot be undone.`}
        isDeleting={bulkDeleteMutation.isPending}
        onClose={() => setBulkDeleteOpen(false)}
        onConfirm={() => bulkDeleteMutation.mutate()}
      />
    </Box>
  )
})

export default CustomersPage
